import type { UseQueryResult } from '@tanstack/react-query'
import {
  CLUB_AWARDS,
  formatClubAwardStanding,
  type ReportAwardsOutput,
  type ReportClubAwardOutput,
  type ReportClubParticipationOutput,
  type TShow,
} from '@schemas'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { clubAwardsVisible } from '@/lib/report-results'

type ClubAwardReport = Pick<ReportAwardsOutput, 'clubSpiritAward' | 'clubChampionAward'>

/**
 * Both club awards in report order, each with its places. A response from a server that
 * predates them, as during a deployment, has neither.
 */
export function clubAwards(report: ClubAwardReport) {
  if (!report.clubSpiritAward || !report.clubChampionAward) return []
  return CLUB_AWARDS.map(item => ({ ...item, award: report[`${item.key}Award`] }))
}

/** The club awards a printed report shows: those the show gives, for the show it is about. */
export function givenClubAwards(
  awards: ReportAwardsOutput | undefined,
  showId: string | undefined,
  currentShow: Pick<TShow, 'enableClubs'> | null | undefined
) {
  return awards && clubAwardsVisible(awards, showId, currentShow)
    ? clubAwards(awards).filter(item => item.award.enabled)
    : []
}

/** Club report rows with a standing: a club with only registered entrants has none yet. */
export function clubsWithStanding(rows: ReportClubParticipationOutput['rows']) {
  return rows.filter(row => row.entryCount > 0 || row.totalPoints !== 0)
}

/** Winners, then runners-up; clubs on equal totals share a place. */
export function clubAwardPlaces(award: ReportClubAwardOutput) {
  return [
    ...award.winners.map(club => ['Winner', club] as const),
    ...award.runnersUp.map(club => ['Runner-up', club] as const),
  ]
}

export function ClubAwardPlaces({
  award,
  measure,
}: {
  award: ReportClubAwardOutput
  measure: 'entries' | 'points'
}) {
  if (award.winners.length === 0) return <p>No club has qualified yet.</p>
  return (
    <>
      {clubAwardPlaces(award).map(([label, club], index) => (
        <p key={`${label}-${index}`}>
          <span className="font-medium">{label}:</span> {formatClubAwardStanding(club, measure)}
        </p>
      ))}
    </>
  )
}

/** Live leaders for both club awards, shown whether or not the show gives them. */
export function ClubAwardsCard({
  query,
}: {
  query: UseQueryResult<ReportClubParticipationOutput, Error>
}) {
  const awards = query.data ? clubAwards(query.data) : []
  return (
    <Card>
      <CardHeader>
        <CardTitle>Club Awards</CardTitle>
        <CardDescription>
          Live club leaders. Club Spirit goes to the club with the most entries and Club Champion to
          the club with the most points.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {query.data ? (
          awards.length ? (
            <div className="grid gap-4 md:grid-cols-2">
              {awards.map(({ title, basis, measure, award }) => (
                <section key={title} aria-label={title} className="space-y-2 rounded-md border p-3">
                  <div>
                    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {title}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {basis}
                      {award.enabled ? '' : ' · Not given at this show'}
                    </p>
                  </div>
                  <div className="space-y-1 text-sm">
                    <ClubAwardPlaces award={award} measure={measure} />
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Club award standings are not available yet. Reload the page after the server update
              finishes.
            </p>
          )
        ) : query.isError ? (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>Unable to load club standings.</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              {query.isFetching ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Loading club standings…
          </p>
        )}
      </CardContent>
    </Card>
  )
}
